import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  ListPageShell, ListPageHeader, ListSearchBar, ListPagination,
} from '../components/ui/ListPageChrome';

const statusStyle = (s) => ({
  'Posted':    'bg-[#E8F4FE] text-[#2184F2]',
  'Cancelled': 'bg-[#F0F4F3] text-[#6B7F7F]',
}[s] || 'bg-[#EDF2F7] text-[#4A6FA5]');

export default function ReceiptFromProduction() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const [receipts, setReceipts]   = useState([]);
  const [loading, setLoading]      = useState(true);
  const [search, setSearch]        = useState('');
  const [page, setPage]            = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]          = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const PER_PAGE = 25;

  const loadReceipts = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/production-orders/all-receipts?page=${page}&limit=${PER_PAGE}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const data = await api.get(url);
      setReceipts(data.rows || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
      setIsAllSelected(false);
    } catch { 
      setReceipts([]); 
    } finally { 
      setLoading(false); 
    }
  }, [page, search, refreshKey]);

  useEffect(() => { 
    loadReceipts(); 
  }, [loadReceipts]);

  const applySearch = (v) => { setSearch(v); setPage(1); };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const fmtAmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleSelectRow = (id) => {
    if (isAllSelected) {
      const next = new Set();
      receipts.forEach(r => {
        if (r.receipt_id !== id) {
          next.add(r.receipt_id);
        }
      });
      setSelectedIds(next);
      setIsAllSelected(false);
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }
  };

  const handleSelectAll = () => {
    const pageAllSelected = receipts.length > 0 && receipts.every(r => selectedIds.has(r.receipt_id));
    if (isAllSelected || pageAllSelected) {
      setIsAllSelected(false);
      setSelectedIds(new Set());
    } else {
      setIsAllSelected(true);
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const count = isAllSelected ? total : selectedIds.size;
    if (count === 0) return;
    const msg = `Are you sure you want to cancel the ${count} selected receipt(s)? This will revert stock increases and production order completed quantities.`;
    if (!window.confirm(msg)) return;

    try {
      if (isAllSelected) {
        await api.post('/production-orders/receipts/bulk-delete', {
          all: true,
          search: search || undefined
        });
      } else {
        await api.post('/production-orders/receipts/bulk-delete', {
          ids: Array.from(selectedIds)
        });
      }
      setSelectedIds(new Set());
      setIsAllSelected(false);

      const newTotal = total - count;
      const newMaxPages = Math.ceil(newTotal / PER_PAGE) || 1;
      if (page > newMaxPages) {
        setPage(newMaxPages);
      } else {
        setRefreshKey(prev => prev + 1);
      }
    } catch (err) {
      alert(err.message || 'Failed to cancel receipts');
    }
  };

  const displayCount = isAllSelected ? total : selectedIds.size;

  return (
    <ListPageShell>
      <ListPageHeader
        title="Receipts from Production"
        subtitle={`${total} record${total !== 1 ? 's' : ''}`}
        action={
          <div className="flex items-center gap-2">
            {isSuperAdmin && displayCount > 0 && (
              <button type="button" onClick={handleBulkDelete} className="btn-danger">
                Cancel Selected ({displayCount})
              </button>
            )}
            <button type="button" onClick={() => navigate('/production/receipt/new')} className="btn-primary">
              + Add New
            </button>
          </div>
        }
      />

      <ListSearchBar value={search} onChange={applySearch} placeholder="Search receipt #, ref 2, remarks…" />

      <div className="data-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              {isSuperAdmin && (
                <th className="text-center px-4 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected || (receipts.length > 0 && receipts.every(r => selectedIds.has(r.receipt_id)))}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                  />
                </th>
              )}
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Receipt #</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Posting Date</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Ref. 2</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Branch</th>
              <th className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Lines</th>
              <th className="text-right px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Total Amount</th>
              <th className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isSuperAdmin ? 8 : 7} className="px-4 py-8 text-center text-sap-textLight">Loading…</td></tr>
            ) : receipts.length === 0 ? (
              <tr><td colSpan={isSuperAdmin ? 8 : 7} className="px-4 py-8 text-center text-sap-textLight">No receipts found.</td></tr>
            ) : receipts.map((r) => (
              <tr
                key={r.receipt_id}
                onClick={() => navigate(`/production/receipt/${r.receipt_id}`)}
                className="table-row even:bg-gray-50/50"
              >
                {isSuperAdmin && (
                  <td className="px-4 py-3 text-center w-12" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isAllSelected || selectedIds.has(r.receipt_id)}
                      onChange={() => handleSelectRow(r.receipt_id)}
                      className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-semibold text-sap-blue">{r.receipt_number}</td>
                <td className="px-4 py-3 text-sap-textLight">{fmtDate(r.posting_date)}</td>
                <td className="px-4 py-3">{r.ref2 || '—'}</td>
                <td className="px-4 py-3 text-sap-textLight">{r.branch || '—'}</td>
                <td className="px-4 py-3 text-center text-sap-textLight">{r.line_count}</td>
                <td className="px-4 py-3 text-right font-semibold">₹{fmtAmt(r.total_amount)}</td>
                <td className="px-4 py-3 text-center">
                  {r.status === 'Posted' ? (
                    <span className="badge-active">{r.status}</span>
                  ) : (
                    <span className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${statusStyle(r.status)}`}>{r.status}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ListPagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
    </ListPageShell>
  );
}
